import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { getClientDomain, translate } from "../../../../utils/Common";
import ignoreAbort from "../../../../utils/ignoreAbort";
import { Card } from "@/components/ui/card";
import Spinner from "@/components/Spinner";
import { cn } from "@/lib/utils";

/**
 * The last locker movements, newest first.
 *
 * Reads /dashboard/packages/transactions/latest, which the two country APIs
 * answer with the same contract. Movements come from ServPlataformasMov;
 * exception reports (type 14) are not movements of goods and the endpoint
 * leaves them out.
 */

// Aligned with the package status palette (`handlerColorPackagesEstatus` in
// utils/Common): a package sitting in a locker is status 3 → warning, a
// delivered one is status 4 → success, a cancelled/expired one is 5-6 → danger.
// A movement that puts goods INTO a locker leaves the package "on boxit", and
// one that takes them out leaves it "delivered", so the two feeds now speak the
// same colour language instead of each inventing its own.
const MOVEMENT_TONE = {
  in: "bg-warning/12 text-warning",
  out: "bg-success/12 text-success",
};

// An admin move is neither: it is an intervention, not part of the normal flow.
const MOVEMENT_TONE_BY_TYPE = {
  ADMIN_MOVEMENT: "bg-info/12 text-info",
};

const MOVEMENT_LABEL = {
  DROP_OFF: "dashboard.home.transactions.drop_off",
  PICK_UP: "dashboard.home.transactions.pick_up",
  COLLECT: "dashboard.home.transactions.collect",
  ADMIN_MOVEMENT: "dashboard.home.transactions.admin_movement",
  DIRECT_RETURN: "dashboard.home.transactions.direct_return",
};

const formatWhen = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const lang = { es: "es", pt: "pt", en: "en" }[localStorage.getItem("language")] || "en";
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  return sameDay
    ? date.toLocaleTimeString(lang, { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString(lang, { day: "2-digit", month: "short" });
};

const LatestTransactions = ({ limit = 10 }) => {
  const history = useHistory();
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);

  const openPackage = (packageId) => {
    if (!packageId) return;
    history.push(`/dashboard/packages/tracking/package/${packageId}`);
  };

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      const response = await fetch(
        `${getClientDomain(2)}/dashboard/packages/transactions/latest?limit=${limit}`,
        {
          method: "GET",
          headers: { authorization: `Bearer ${sessionStorage.getItem("token")}` },
          signal: controller.signal,
        }
      );

      // The endpoint may not be deployed yet in every environment; an older API
      // answers 404 with an HTML body, so never parse before checking.
      if (!response.ok) {
        setFailed(true);
        setRows([]);
        return;
      }

      const result = await response.json();
      setRows(Array.isArray(result?.payload) ? result.payload : []);
    };

    load().catch(ignoreAbort);
    return () => controller.abort();
  }, [limit]);

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h3 className="m-0 text-sm font-semibold text-foreground">
          {translate("dashboard.home.transactions.title")}
        </h3>
        {rows?.length > 0 && (
          <span className="text-[11px] text-muted-foreground">{rows.length}</span>
        )}
      </div>

      {rows === null ? (
        <div className="py-6 text-center">
          <Spinner type={1} />
        </div>
      ) : rows.length === 0 ? (
        <p className="m-0 py-6 text-center text-sm text-muted-foreground">
          {failed
            ? translate("dashboard.home.transactions.unavailable")
            : translate("dashboard.home.transactions.empty")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-border">
                {[
                  "dashboard.home.transactions.when",
                  "dashboard.home.transactions.movement",
                  "dashboard.packages.loadpackages.formProvider.numPackage",
                  "dashboard.home.status_platforms.platform",
                  "global.client",
                ].map((key) => (
                  <th
                    key={key}
                    className="whitespace-nowrap px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                  >
                    {translate(key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  // Navigable, not just clickable: a row that only answers to the
                  // mouse is invisible to the keyboard. Rows without a package id
                  // stay inert rather than pretending to be a link.
                  role={row.packageId ? "link" : undefined}
                  tabIndex={row.packageId ? 0 : undefined}
                  onClick={() => openPackage(row.packageId)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openPackage(row.packageId);
                    }
                  }}
                  className={cn(
                    "border-b border-border/60 transition-colors last:border-0",
                    row.packageId
                      ? "cursor-pointer hover:bg-accent/40 focus-visible:outline-none focus-visible:bg-accent/60"
                      : "opacity-90"
                  )}
                >
                  <td className="whitespace-nowrap px-2 py-2 text-xs text-muted-foreground">
                    {formatWhen(row.date)}
                  </td>
                  <td className="px-2 py-2">
                    <span
                      className={cn(
                        "inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold",
                        MOVEMENT_TONE_BY_TYPE[row.movement] ||
                          MOVEMENT_TONE[row.direction] ||
                          "bg-muted text-muted-foreground"
                      )}
                    >
                      {MOVEMENT_LABEL[row.movement]
                        ? translate(MOVEMENT_LABEL[row.movement])
                        : row.movement}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-[13px] font-medium text-foreground">
                    {row.packageNumber || row.tracking || "—"}
                  </td>
                  <td className="max-w-[16rem] truncate px-2 py-2 text-[13px] text-muted-foreground">
                    {row.locker || "—"}
                  </td>
                  <td className="max-w-[14rem] truncate px-2 py-2 text-[13px] text-muted-foreground">
                    {[row.clientName, row.clientLastName].filter(Boolean).join(" ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default LatestTransactions;
